import "./styles.scss";

import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";

import ModalNew from "components/ModalNew";
import FormikInput from "components/formik/FormikInput";
import FormikTextarea from "components/formik/FormikTextarea";

const mainClass = "access-data-modal";

const AccessDataModal = ({
  website,
  data,
  updateData,
  isOpen,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const formikRef = useRef(null);
  const initialValues = useMemo(
    () => ({
      wordpress: {
        url: data?.wordpress?.url || "",
        login: data?.wordpress?.login || "",
        password: data?.wordpress?.password || "",
      },
      server: {
        host: data?.server?.host || "",
        username: data?.server?.username || "",
        password: data?.server?.password || "",
      },
      database: {
        name: data?.database?.name || "",
        username: data?.database?.username || "",
        password: data?.database?.password || "",
        host: data?.database?.host || "",
      },
      notes: data?.notes || "",
    }),
    [data]
  );

  useEffect(() => {
    if (isOpen) {
      formikRef.current?.resetForm({ values: initialValues });
    }
  }, [isOpen, initialValues]);

  const handleSubmit = async (values, setSubmitting) => {
    if (typeof updateData !== "function") return;

    try {
      await updateData(values);

      if (typeof onClose === "function") onClose();
      if (typeof onSubmit === "function") onSubmit();
    } catch (err) {
      setSubmitting(false);

      if (typeof onClose === "function") onClose();
      if (typeof onSubmit === "function") onSubmit();
    }
  };

  return (
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        onSubmit={(values, { setSubmitting }) => {
          handleSubmit(values, setSubmitting);
        }}
      >
        {({ dirty, isSubmitting, submitForm }) => (
          <ModalNew
              title={
                t("See/Change access data") + (website ? ` - ${website}` : "")
              }
              description={t(
                "Update access credentials for the WordPress CMS, FTP server, database, or other accounts."
              )}
              variant="full"
              loading={isSubmitting}
              isOpen={isOpen}
              isSubmitDisabled={!dirty}
              onClose={onClose}
              onSubmit={submitForm}
            >
              <Form className={mainClass} autoComplete="off">
              <div className={`${mainClass}__row`}>
                <div className={`${mainClass}__column`}>
                  <div className={`${mainClass}__column-header`}>
                    <h4>{t("CMS WordPress")}</h4>
                  </div>
                  <FormikInput
                    name="wordpress.url"
                    label={t("Login URL")}
                    placeholder="login.webinity.com"
                    disabled={isSubmitting}
                  />
                  <FormikInput
                    name="wordpress.login"
                    label={t("Login")}
                    placeholder="webinity"
                    disabled={isSubmitting}
                  />
                  <FormikInput
                    name="wordpress.password"
                    label={t("Password")}
                    placeholder="***************"
                    disabled={isSubmitting}
                  />
                </div>
                <div className={`${mainClass}__column`}>
                  <div className={`${mainClass}__column-header`}>
                    <h4>{t("Server / FTP")}</h4>
                  </div>
                  <FormikInput
                    name="server.host"
                    label={t("Host")}
                    placeholder="login.webinity.com"
                    disabled={isSubmitting}
                  />
                  <FormikInput
                    name="server.username"
                    label={t("Username")}
                    placeholder="webinity"
                    disabled={isSubmitting}
                  />
                  <FormikInput
                    name="server.password"
                    label={t("Password")}
                    placeholder="***************"
                    disabled={isSubmitting}
                  />
                </div>
                <div className={`${mainClass}__column`}>
                  <div className={`${mainClass}__column-header`}>
                    <h4>{t("Database")}</h4>
                  </div>
                  <FormikInput
                    name="database.host"
                    label={t("Host/URL")}
                    placeholder="login.webinity.com"
                    disabled={isSubmitting}
                  />
                  <FormikInput
                    name="database.name"
                    label={t("Database name")}
                    placeholder="webinity_db"
                    disabled={isSubmitting}
                  />
                  <FormikInput
                    name="database.username"
                    label={t("Username")}
                    placeholder="webinity"
                    disabled={isSubmitting}
                  />
                  <FormikInput
                    name="database.password"
                    label={t("Password")}
                    placeholder="***************"
                    disabled={isSubmitting}
                  />
                </div>
                <div className={`${mainClass}__column`}>
                  <div className={`${mainClass}__column-header`}>
                    <h4>{t("Additional access data")}</h4>
                  </div>
                  <FormikTextarea
                    name="notes"
                    label={t("Github, Bitbucket etc.")}
                    placeholder={t(
                      "Enter any additional information that doesn't fit into the categories listed above."
                    )}
                  />
                </div>
              </div>
              </Form>
            </ModalNew>
        )}
      </Formik>
  );
};

export default AccessDataModal;
